import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import {
  Box, Typography, Tabs, Tab, CircularProgress, Alert,
  Button, Dialog, DialogTitle, DialogContent, DialogActions, Divider,
} from '@mui/material';
import CheckIcon from '@mui/icons-material/Check';
import SyncIcon from '@mui/icons-material/Sync';
import { DndProvider } from 'react-dnd';
import { HTML5Backend } from 'react-dnd-html5-backend';

import { KNOWN_CATEGORIES, CATEGORY_LABELS } from '../productEditConstants';
import { useProductConfig } from './useProductConfig';
import { useSaveProductConfig } from './useSaveProductConfig';
import CategoryConfigPanel from './CategoryConfigPanel';
import { deleteCategoryConfig } from '../../databaseManager';

export default function ProductSettingsPage() {
  const { customer } = useParams();
  const [activeBodyHtmlCategory, setActiveBodyHtmlCategory] = useState('default');
  const [resetTarget, setResetTarget] = useState(null); // 'columns' | category key

  // ── Global column config (always 'default') ─────────────────────────────────
  const {
    config: colConfig, setConfig: setColConfig,
    loading: colLoading, error: colError, refreshConfig: refreshColConfig,
  } = useProductConfig(customer, 'default');
  const { save: saveColConfig, saving: savingCols, lastSaved: colLastSaved } = useSaveProductConfig(customer, 'default');

  // ── Per-category body HTML config ───────────────────────────────────────────
  const {
    config: htmlConfig, setConfig: setHtmlConfig,
    loading: htmlLoading, error: htmlError, refreshConfig: refreshHtmlConfig,
  } = useProductConfig(customer, activeBodyHtmlCategory);
  const { save: saveHtmlConfig, saving: savingHtml, lastSaved: htmlLastSaved } = useSaveProductConfig(customer, activeBodyHtmlCategory);

  const handleColChange = (updated) => {
    setColConfig(updated);
    saveColConfig(updated);
  };

  const handleHtmlChange = (updated) => {
    setHtmlConfig(updated);
    saveHtmlConfig(updated);
  };

  const [resetting, setResetting] = useState(false);

  const handleReset = async () => {
    if (!resetTarget) return;
    setResetting(true);
    try {
      if (resetTarget === 'columns') {
        await deleteCategoryConfig(customer, 'default');
        await refreshColConfig();
      } else {
        await deleteCategoryConfig(customer, resetTarget);
        await refreshHtmlConfig();
      }
    } finally {
      setResetting(false);
      setResetTarget(null);
    }
  };

  const resetLabel = resetTarget === 'columns'
    ? 'table columns'
    : CATEGORY_LABELS[resetTarget] || resetTarget;

  return (
    <DndProvider backend={HTML5Backend}>
      <Box sx={{ p: 3, maxWidth: 1400 }}>
        <Typography variant="h5" sx={{ fontWeight: 600, mb: 1 }}>Product Settings</Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 4 }}>
          Table columns are configured globally — one set of columns for all products. Body HTML templates are configured per category.
        </Typography>

        {/* ── Table Columns ────────────────────────────────────────────────── */}
        <Box sx={{ mb: 5 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 2, gap: 2 }}>
            <Typography variant="h6" sx={{ fontWeight: 600, flex: 1 }}>Table Columns</Typography>
            <SaveStatus saving={savingCols} lastSaved={colLastSaved} />
            <Button
              size="small" variant="outlined" color="error"
              onClick={() => setResetTarget('columns')}
              disabled={colLoading || resetting}
            >
              Reset to Defaults
            </Button>
          </Box>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Drag to reorder. Toggle to show/hide. Add custom columns mapped to any product spec field.
            These settings apply to all products regardless of category.
          </Typography>
          {colError && <Alert severity="warning" sx={{ mb: 2 }}>Could not load saved config — showing defaults.</Alert>}
          {colLoading ? (
            <Box sx={{ py: 4, textAlign: 'center' }}><CircularProgress /></Box>
          ) : (
            <CategoryConfigPanel config={colConfig} onChange={handleColChange} mode="columns" />
          )}
        </Box>

        <Divider sx={{ mb: 5 }} />

        {/* ── Body HTML Templates ──────────────────────────────────────────── */}
        <Box>
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 2, gap: 2 }}>
            <Typography variant="h6" sx={{ fontWeight: 600, flex: 1 }}>Body HTML Templates</Typography>
            <SaveStatus saving={savingHtml} lastSaved={htmlLastSaved} />
            <Button
              size="small" variant="outlined" color="error"
              onClick={() => setResetTarget(activeBodyHtmlCategory)}
              disabled={htmlLoading || resetting}
            >
              Reset to Defaults
            </Button>
          </Box>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            These fields appear as bullet points in the Body HTML. Configured per product category.
          </Typography>

          <Tabs
            value={activeBodyHtmlCategory}
            onChange={(_, v) => setActiveBodyHtmlCategory(v)}
            sx={{ mb: 3, borderBottom: 1, borderColor: 'divider' }}
          >
            {KNOWN_CATEGORIES.map((key) => (
              <Tab key={key} value={key} label={CATEGORY_LABELS[key] || key} />
            ))}
          </Tabs>

          {htmlError && <Alert severity="warning" sx={{ mb: 2 }}>Could not load saved config — showing defaults.</Alert>}
          {htmlLoading ? (
            <Box sx={{ py: 4, textAlign: 'center' }}><CircularProgress /></Box>
          ) : (
            <CategoryConfigPanel config={htmlConfig} onChange={handleHtmlChange} mode="bodyHtml" />
          )}
        </Box>
      </Box>

      <Dialog open={!!resetTarget} onClose={() => setResetTarget(null)} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ color: 'error.main' }}>Reset to Defaults?</DialogTitle>
        <DialogContent>
          <Typography>
            This will restore the built-in defaults for <strong>{resetLabel}</strong>. Any custom configuration will be lost.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setResetTarget(null)}>Cancel</Button>
          <Button onClick={handleReset} color="error" variant="contained" disabled={resetting}>
            {resetting ? 'Resetting…' : 'Reset'}
          </Button>
        </DialogActions>
      </Dialog>
    </DndProvider>
  );
}

function SaveStatus({ saving, lastSaved }) {
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    if (lastSaved) {
      setVisible(true);
      const t = setTimeout(() => setVisible(false), 3000);
      return () => clearTimeout(t);
    }
  }, [lastSaved]);

  if (saving) {
    return (
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, color: 'text.secondary', fontSize: '0.8rem' }}>
        <SyncIcon fontSize="small" sx={{ animation: 'spin 1s linear infinite', '@keyframes spin': { from: { transform: 'rotate(0deg)' }, to: { transform: 'rotate(360deg)' } } }} />
        Saving…
      </Box>
    );
  }
  if (visible) {
    return (
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, color: 'success.main', fontSize: '0.8rem' }}>
        <CheckIcon fontSize="small" />
        Saved
      </Box>
    );
  }
  return null;
}
